import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { contactEndpoints } from '@/lib/api/endpoints';
import { apiDelete, operationsClient } from '@/lib/api';

/**
 * NOT-677 — deleting a contact is refused, on purpose, while it still has open
 * invoices, active jobs or an active membership: the server answers 409 naming
 * the blockers ("Complete or cancel 2 active jobs before deleting").
 *
 * This used to drop the row from every cached list in `onMutate` and restore it
 * in `onError`. On the refused path — the common one, because a contact worth
 * deleting usually has work attached — the operator watched the contact
 * disappear, navigated away, came back, and found it there again with its jobs
 * and estimates intact. That reads as a delete that half worked, and it was
 * reported as silent partial deletion rather than as the refusal it is.
 *
 * A delete here is deliberate, infrequent, and issued against a server that
 * genuinely rejects it, so there is nothing to gain from showing it as done
 * before it is. Wait for the server: on success the lists are invalidated and
 * the row goes once; on refusal nothing moves and the toast `useApiMutation`
 * already raises carries the reason.
 */
export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, contactEndpoints.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact deleted');
    },
  });
}
